import { useMemo, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/stores/toast-store';
import { apiErrorMessage } from '@/lib/api-error';
import {
  useAdminCreateFundingAccount,
  useAdminUpdateFundingAccount,
  type AdminFundingAccount,
  type AdminFundingAccountInput,
  type AdminFundingSide,
} from '@/hooks/use-admin-funding-accounts';

/**
 * Super-admin fallback for funding accounts: add one when the tenant gave no
 * bank details at onboarding, or fix one whose details don't match the bank.
 * The normal path (onboarding fan-out) is unchanged.
 */
type Props = {
  tenantId: string;
  locationId: string;
  onClose: () => void;
} & (
  | {
      mode: 'create';
      /** Sides pre-ticked — the ones missing an account. */
      defaultSides: AdminFundingSide[];
      /** Onboarding bank details, used as a starting point when present. */
      prefill?: { bankName: string; routingNumber: string; accountNumber: string } | null;
    }
  | { mode: 'edit'; side: AdminFundingSide; account: AdminFundingAccount }
);

const SIDE_LABEL: Record<AdminFundingSide, string> = { send: 'Send payments', receive: 'Receive payments' };

const selectClass = 'mt-1 h-9 w-full rounded-lg border border-input bg-white px-3 text-sm';

function validate(f: AdminFundingAccountInput): Partial<Record<keyof AdminFundingAccountInput, string>> {
  const e: Partial<Record<keyof AdminFundingAccountInput, string>> = {};
  if (!f.nickname.trim()) e.nickname = 'Nickname is required';
  if (f.bankName.trim().length < 3) e.bankName = 'Bank name must be at least 3 characters';
  if (!f.accountHolderName.trim()) e.accountHolderName = 'Account holder name is required';
  if (!/^\d{9}$/.test(f.routingNumber)) e.routingNumber = 'Routing number must be exactly 9 digits';
  if (!/^\d{4,17}$/.test(f.accountNumber)) e.accountNumber = 'Account number must be 4 to 17 digits';
  return e;
}

export function AdminFundingAccountModal(props: Props) {
  const { tenantId, locationId, onClose } = props;
  const create = useAdminCreateFundingAccount(tenantId, locationId);
  const update = useAdminUpdateFundingAccount(tenantId, locationId);
  const isEdit = props.mode === 'edit';

  const [form, setForm] = useState<AdminFundingAccountInput>(() => {
    if (props.mode === 'edit') {
      const a = props.account;
      return {
        nickname: a.nickname,
        bankName: a.bankName,
        accountType: a.accountType === 'savings' ? 'savings' : 'checking',
        routingNumber: a.routingNumber,
        accountNumber: a.accountNumber,
        accountHolderName: a.accountHolderName,
        accountBusinessType: a.accountBusinessType === 'personal' ? 'personal' : 'business',
      };
    }
    return {
      nickname: 'Primary',
      bankName: props.prefill?.bankName ?? '',
      accountType: 'checking',
      routingNumber: props.prefill?.routingNumber ?? '',
      accountNumber: props.prefill?.accountNumber ?? '',
      accountHolderName: '',
      accountBusinessType: 'business',
    };
  });
  const [sides, setSides] = useState<AdminFundingSide[]>(props.mode === 'create' ? props.defaultSides : []);
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => validate(form), [form]);
  const sidesError = !isEdit && sides.length === 0 ? 'Select Send, Receive, or both' : '';
  const hasErrors = Object.keys(errors).length > 0 || !!sidesError;
  const pending = create.isPending || update.isPending;

  const set = <K extends keyof AdminFundingAccountInput>(key: K, value: AdminFundingAccountInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const digits = (v: string) => v.replace(/\D/g, '');
  const showError = (key: keyof AdminFundingAccountInput) => (submitted ? errors[key] : undefined);

  const handleSubmit = async () => {
    setSubmitted(true);
    if (hasErrors) return;
    try {
      if (props.mode === 'edit') {
        await update.mutateAsync({ side: props.side, id: props.account.id, ...form });
        toast.success('Funding account updated');
      } else {
        const res = await create.mutateAsync({ ...form, sides });
        const failed = Object.entries(res.errors ?? {});
        if (failed.length) {
          // Partial success: one side saved, the other didn't.
          const [side, msg] = failed[0];
          toast.error(`${SIDE_LABEL[side as AdminFundingSide]} not saved: ${msg}`);
        } else {
          toast.success('Funding account added');
        }
      }
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err, isEdit ? 'Failed to update funding account' : 'Failed to add funding account'));
    }
  };

  const field = (
    key: 'nickname' | 'bankName' | 'accountHolderName' | 'routingNumber' | 'accountNumber',
    label: string,
    opts: { numeric?: boolean; maxLength?: number } = {},
  ) => (
    <div>
      <Label htmlFor={`fa-${key}`}>{label}</Label>
      <Input
        id={`fa-${key}`}
        className="mt-1"
        value={form[key]}
        inputMode={opts.numeric ? 'numeric' : undefined}
        maxLength={opts.maxLength}
        error={!!showError(key)}
        onChange={(e) => set(key, opts.numeric ? digits(e.target.value) : e.target.value)}
      />
      {showError(key) && <p className="mt-1 text-xs text-red-600">{showError(key)}</p>}
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit ${SIDE_LABEL[props.side].toLowerCase()} account` : 'Add funding account'}
      subtitle={
        isEdit
          ? 'Correct the bank details for this account.'
          : 'For locations with missing or mismatched onboarding bank details. Added as the primary account.'
      }
      widthClass="max-w-[560px]"
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={pending}>
            {pending ? 'Saving...' : isEdit ? 'Save changes' : 'Add account'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {!isEdit && props.prefill && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Prefilled from the onboarding form. Verify with the tenant before saving.
          </p>
        )}

        {!isEdit && (
          <div>
            <Label>Use for</Label>
            <div className="mt-1 flex gap-4">
              {(['send', 'receive'] as const).map((side) => (
                <label key={side} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={sides.includes(side)}
                    onChange={(e) =>
                      setSides((prev) => (e.target.checked ? [...prev, side] : prev.filter((s) => s !== side)))
                    }
                  />
                  {SIDE_LABEL[side]}
                </label>
              ))}
            </div>
            {submitted && sidesError && <p className="mt-1 text-xs text-red-600">{sidesError}</p>}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {field('nickname', 'Nickname')}
          {field('bankName', 'Bank name')}
          {field('accountHolderName', 'Account holder name')}
          <div>
            <Label htmlFor="fa-accountBusinessType">Account holder type</Label>
            <select
              id="fa-accountBusinessType"
              className={selectClass}
              value={form.accountBusinessType}
              onChange={(e) => set('accountBusinessType', e.target.value as 'business' | 'personal')}
            >
              <option value="business">Business</option>
              <option value="personal">Personal</option>
            </select>
          </div>
          {field('routingNumber', 'Routing number', { numeric: true, maxLength: 9 })}
          {field('accountNumber', 'Account number', { numeric: true, maxLength: 17 })}
          <div>
            <Label htmlFor="fa-accountType">Account type</Label>
            <select
              id="fa-accountType"
              className={selectClass}
              value={form.accountType}
              onChange={(e) => set('accountType', e.target.value as 'checking' | 'savings')}
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>
        </div>
      </div>
    </Modal>
  );
}
